
import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6e3oieqd308q1a0acdg';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    asunto: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isValid = formData.nombre.trim() !== '' && formData.email.trim() !== '' && formData.asunto !== '' && formData.mensaje.trim() !== '' && formData.mensaje.length <= 500;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('nombre', formData.nombre);
      body.append('email', formData.email);
      body.append('telefono', formData.telefono);
      body.append('asunto', formData.asunto);
      body.append('mensaje', formData.mensaje);

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nombre: '', email: '', telefono: '', asunto: '', mensaje: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 4000);
      }
    } catch {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 4000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 md:py-20 bg-neutral-900 relative">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Left side - info */}
          <div className="flex flex-col justify-center">
            <span className="inline-block text-amber-500 text-xs font-bold uppercase tracking-widest mb-3">
              ¿Tienes alguna pregunta?
            </span>
            <h2
              className="text-[36px] sm:text-[48px] md:text-[60px] leading-none font-bold text-white mb-6"
              style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '3px' }}
            >
              ESCRÍBENOS
            </h2>
            <p className="text-neutral-400 text-sm md:text-base leading-relaxed mb-8 max-w-lg">
              Ya sea para pedir presupuesto, resolver dudas sobre el proceso de tatuaje, consultar disponibilidad o proponer una colaboración artística, estamos aquí para ayudarte.
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-600/15 rounded-lg flex-shrink-0">
                  <i className="ri-chat-smile-2-line text-amber-500 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-white font-medium text-sm mb-1">Consulta sin compromiso</h4>
                  <p className="text-neutral-500 text-sm">Cuéntanos tu idea y te asesoramos gratis sobre estilos, tamaños y precios.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-600/15 rounded-lg flex-shrink-0">
                  <i className="ri-timer-line text-amber-500 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-white font-medium text-sm mb-1">Respuesta rápida</h4>
                  <p className="text-neutral-500 text-sm">Respondemos todos los mensajes en menos de 24 horas laborables.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-600/15 rounded-lg flex-shrink-0">
                  <i className="ri-shield-check-line text-amber-500 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-white font-medium text-sm mb-1">Privacidad garantizada</h4>
                  <p className="text-neutral-500 text-sm">Tus datos están protegidos según el RGPD. No compartimos tu información.</p>
                </div>
              </div>
            </div>

            {/* Studio image */}
            <div className="mt-10 rounded-lg overflow-hidden border border-neutral-700/50 w-full h-[220px]">
              <img
                src="https://public.readdy.ai/ai/img_res/4e5a87676d9b608e1d85b25e3c0fcf30.jpg"
                alt="Interior del estudio Daro Tattoo en Adra"
                title="Daro Tattoo estudio tatuajes Adra"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right side - form */}
          <div className="bg-neutral-800/60 border border-neutral-700/50 rounded-lg p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-send-line text-white text-lg"></i>
              </div>
              <h3
                className="text-xl md:text-2xl font-bold text-white"
                style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}
              >
                FORMULARIO DE CONTACTO
              </h3>
            </div>

            <form id="contact-page-form" onSubmit={handleSubmit} data-readdy-form className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Nombre completo *</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    placeholder="Tu nombre"
                    className="w-full px-4 py-3 bg-neutral-900 border border-neutral-600 rounded-lg text-white placeholder-neutral-500 focus:border-amber-500 focus:outline-none transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="tu@email.com"
                    className="w-full px-4 py-3 bg-neutral-900 border border-neutral-600 rounded-lg text-white placeholder-neutral-500 focus:border-amber-500 focus:outline-none transition-colors text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Teléfono</label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="+34 600 000 000"
                    className="w-full px-4 py-3 bg-neutral-900 border border-neutral-600 rounded-lg text-white placeholder-neutral-500 focus:border-amber-500 focus:outline-none transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Asunto *</label>
                  <select
                    name="asunto"
                    value={formData.asunto}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-neutral-900 border border-neutral-600 rounded-lg text-white focus:border-amber-500 focus:outline-none transition-colors text-sm cursor-pointer"
                  >
                    <option value="">Selecciona un asunto</option>
                    <option value="Presupuesto tatuaje">Presupuesto tatuaje</option>
                    <option value="Consulta general">Consulta general</option>
                    <option value="Disponibilidad">Disponibilidad</option>
                    <option value="Cover-up">Cover-up / Tapado</option>
                    <option value="Arte urbano">Arte urbano / Mural</option>
                    <option value="Colaboración">Colaboración artística</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Mensaje *</label>
                <textarea
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="Cuéntanos en qué podemos ayudarte..."
                  className="w-full px-4 py-3 bg-neutral-900 border border-neutral-600 rounded-lg text-white placeholder-neutral-500 focus:border-amber-500 focus:outline-none transition-colors text-sm resize-none"
                ></textarea>
                <p className="text-xs text-neutral-500 mt-1 text-right">{formData.mensaje.length}/500</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !isValid}
                className="w-full py-4 bg-amber-600 text-white font-bold uppercase tracking-wider hover:bg-amber-500 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                style={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  letterSpacing: '2px',
                  clipPath: 'polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px)'
                }}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Enviando...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill"></i>
                    Enviar Mensaje
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-900/50 border border-green-500 rounded-lg text-green-400 text-sm flex items-center gap-2">
                  <i className="ri-check-double-line text-xl"></i>
                  <div>
                    <strong>¡Mensaje enviado!</strong> Te responderemos lo antes posible.
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-400 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line text-xl"></i>
                  <div>
                    <strong>Error al enviar.</strong> Por favor, inténtalo de nuevo.
                  </div>
                </div>
              )}
            </form>

            <p className="text-neutral-600 text-xs mt-4 text-center">
              Al enviar este formulario aceptas nuestra{' '}
              <a href="/politica-privacidad" className="underline hover:text-neutral-400 cursor-pointer">política de privacidad</a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
